import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import RestoreIcon from '@mui/icons-material/Restore';
import type { WearableAlertSettings } from '../../constants/wearableAlertSettings';

type Props = {
  value: WearableAlertSettings;
  onChange: (next: WearableAlertSettings) => void;
  onSave: () => void;
  onResetDefaults: () => void;
  saving?: boolean;
  cardSx?: SxProps<Theme>;
};

const WearableAlertSettingsCard: React.FC<Props> = ({
  value,
  onChange,
  onSave,
  onResetDefaults,
  saving = false,
  cardSx,
}) => (
  <Card sx={cardSx}>
    <CardHeader
      title="웨어러블·시설 PC 알림"
      subheader="웨어러블·시설 PC WebSocket으로 현장 안전 알림을 보냄. 표시 시간(초)은 PC 팝업 자동 닫힘에도 적용됨."
      action={
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<RestoreIcon />}
            onClick={onResetDefaults}
            disabled={saving}
          >
            기본값
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={onSave}
            disabled={saving}
          >
            {saving ? '저장 중…' : '저장'}
          </Button>
        </Box>
      }
    />
    <CardContent>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={value.default_selected}
                onChange={(e) => onChange({ ...value, default_selected: e.target.checked })}
              />
            }
            label="관제 팝업에서 기본 선택"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth margin="dense">
            <InputLabel>대상 기기</InputLabel>
            <Select
              label="대상 기기"
              value={value.target}
              onChange={(e) =>
                onChange({
                  ...value,
                  target: e.target.value as WearableAlertSettings['target'],
                })
              }
            >
              <MenuItem value="pool_active_devices">시설 활성 웨어러블 전체</MenuItem>
              <MenuItem value="connected_only">현재 WebSocket 연결된 기기만</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth margin="dense">
            <InputLabel>진동 패턴</InputLabel>
            <Select
              label="진동 패턴"
              value={value.vibrate_pattern}
              onChange={(e) =>
                onChange({
                  ...value,
                  vibrate_pattern: e.target.value as WearableAlertSettings['vibrate_pattern'],
                })
              }
            >
              <MenuItem value="short">짧게</MenuItem>
              <MenuItem value="long">길게</MenuItem>
              <MenuItem value="pulse">펄스</MenuItem>
              <MenuItem value="sos">SOS</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <TextField
            fullWidth
            margin="dense"
            label="현장 PC 팝업·웨어러블 표시 시간(초)"
            type="number"
            inputProps={{ min: 3, max: 120, step: 1 }}
            value={value.alert_duration_sec}
            onChange={(e) =>
              onChange({ ...value, alert_duration_sec: Number(e.target.value) || 15 })
            }
            helperText="시설 PC 위험 알림 팝업이 자동으로 닫히기까지 시간 (3~120초)"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            margin="dense"
            label="WebSocket type 필드"
            value={value.message_type}
            onChange={(e) => onChange({ ...value, message_type: e.target.value })}
            helperText="기기 펌웨어가 수신하는 JSON type (기본 safety_alert)"
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={value.include_pool_name}
                onChange={(e) => onChange({ ...value, include_pool_name: e.target.checked })}
              />
            }
            label="메시지에 시설명 포함"
          />
          <FormControlLabel
            control={
              <Switch
                checked={value.broadcast_pool_websocket}
                onChange={(e) =>
                  onChange({ ...value, broadcast_pool_websocket: e.target.checked })
                }
              />
            }
            label="풀 WebSocket에도 동일 payload"
          />
        </Grid>
      </Grid>
    </CardContent>
  </Card>
);

export default WearableAlertSettingsCard;
